import { GcpBox, GcpOtpInput, GcpTypography } from '@back-office/ui';
import { Button } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Alert } from '@material-ui/lab';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useViewModel from './viewmodel';

export interface EnterOTPProps {
  setAuth: (userIndex: number) => void;
}

type ErrorAlertProps = {
  anyError: boolean;
  anyTimeLeft: boolean;
};

export enum OtpVia {
  SMS = 'SMS',
  EMAIL = 'EMAIL',
}

const useStyles = makeStyles(
  (theme) => ({
    link: {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    button: {
      color: theme.palette.common.hitGray,
    },
    alert: {
      width: '50%',
    },
    resend_otp_option: {
      textAlign: 'center',
    },
  }),
  { name: 'EnterOTP' }
);

const EnterOTP: FC<EnterOTPProps> = observer(({ setAuth }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  // override button style
  const buttonStyle = {
    padding: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    transition: 'none',
  };

  const {
    otpMode,
    otpModeMap,
    state,
    handleOtpInputChange,
    handleResendOtp,
    otpRef,
  } = useViewModel({ setAuth });

  return (
    <>
      <GcpBox mt={3} textAlign="center">
        <GcpTypography variant="h3">{t('otp_input.enter')}</GcpTypography>
      </GcpBox>
      <GcpBox mt={3} textAlign="center">
        <GcpTypography paint="hitGray" variant="body1">
          {t('otp_input.send_to', {
            email: otpModeMap[otpMode].receiverText,
          })}
        </GcpTypography>
      </GcpBox>
      <GcpBox display="flex" justifyContent="center" mt={3}>
        <GcpBox width="250px">
          <GcpTypography paint="hitGray" variant="body1">
            {t('otp_input.ref', { code: otpRef })}
          </GcpTypography>
        </GcpBox>
      </GcpBox>
      <GcpBox display="flex" justifyContent="center" mt={1}>
        <GcpOtpInput
          error={state.error}
          handleChange={handleOtpInputChange}
          value={state.otp}
        />
      </GcpBox>
      <ErrorAlert
        anyError={state.error}
        anyTimeLeft={Boolean(state.countdownTime)}
      />
      <GcpBox display="flex" justifyContent="center" mt={5}>
        <GcpBox className={classes.resend_otp_option}>
          <GcpBox display="flex">
            <GcpTypography paint="hitGray" variant="body1">
              {t('otp_input.timeout')}
            </GcpTypography>
            &nbsp;
            {state.countdownTime ? (
              <GcpTypography paint="hitGray" variant="body1">
                {t('otp_input.remaining', {
                  second: state.countdownTime,
                })}
              </GcpTypography>
            ) : (
              <Button
                disableFocusRipple
                disableRipple
                className={classes.link}
                color="secondary"
                role="button"
                style={buttonStyle}
                onClick={() => handleResendOtp()}
              >
                {t('otp_input.resend')}
              </Button>
            )}
          </GcpBox>
          {state.countdownTime <=
            otpModeMap?.[otpMode]?.timeToShowToggleBtn && (
            <GcpTypography paint="hitGray" variant="body1">
              {t('otp_input.or')}&nbsp;
              <Button
                disableFocusRipple
                disableRipple
                className={classes.link}
                color="secondary"
                role="button"
                style={buttonStyle}
                type="button"
                variant="text"
                onClick={() => handleResendOtp(true)}
              >
                {otpModeMap[otpMode].toggleOtpModeText}
              </Button>
            </GcpTypography>
          )}
        </GcpBox>
      </GcpBox>
    </>
  );
});

const ErrorAlert: FC<ErrorAlertProps> = ({ anyError, anyTimeLeft }) => (
  <>
    {anyError && (
      <GcpBox display="flex" justifyContent="center" mt={3}>
        <Alert icon={false} severity="error" style={{ width: 320 }}>
          <GcpTypography paint="flamingo" variant="body1">
            {anyTimeLeft ? 'Invalid OTP code' : 'OTP code is expired'}
          </GcpTypography>
        </Alert>
      </GcpBox>
    )}
  </>
);

export default EnterOTP;
